// Spanish catalog. Mirrors en.ts. Anything missing here falls back to English.
//
// IMPORTANT — safety content: Spanish is spoken across many countries, each with
// its own crisis numbers, so this does NOT invent a national suicide hotline.
// It routes to the universal 112 emergency line (reachable on any GSM phone and
// routed to local emergency in most countries) plus an international directory of
// crisis lines. Before selling into a specific country, a person should replace
// this with that country's verified resources. Machine translation is not enough
// for safety-critical copy.

import type { Catalog } from './en';

const es: Catalog = {
  common: {
    seeDemo: 'Ver demo',
    logIn: 'Iniciar sesión',
    startFree: 'Empezar gratis',
    back: 'Atrás',
    contactUs: 'Contáctanos',
    getSchoolLicense: 'Obtener una licencia escolar',
    language: 'Idioma',
  },

  languagePicker: {
    label: 'Idioma',
    availableNow: 'Disponible ahora',
    comingSoon: 'Próximamente',
  },

  landing: {
    heroBadge: 'Prueba gratis de 7 días · Sin tarjeta',
    heroTitle: 'Por fin entiende por qué sus estudiantes hacen lo que hacen.',
    heroSubtitle: 'Decode My Student ofrece a cada docente de K-12 acceso inmediato a la ciencia del cerebro, estrategias de conducta y apoyo personal — para cada dificultad de cada estudiante, todos los días.',
    startTrial: 'Empezar prueba gratis de 7 días',
    pathPrompt: 'Empiece gratis — elija lo que le corresponde:',
    teacherPath: '👩‍🏫 Soy docente',
    parentPath: '👪 Soy madre o padre',
    seeDemo: 'Ver una demo en vivo — sin iniciar sesión',
    skipTrial: 'Omitir la prueba — suscríbete ahora ($29/mes o $299/año)',
    noCard: 'No se requiere tarjeta para la prueba · Acceso completo desde el primer día',

    credibilityQuote: '"Creado por el fundador de We R H.O.P.E., el primer programa de coaching de salud cerebral integrado en escuelas de los Estados Unidos. Basado en la neurociencia y la práctica informada sobre el trauma."',
    badgeNeuro: 'Basado en la neurociencia',
    badgeTrauma: 'Informado sobre el trauma',

    problemsTitle: '¿Le suena familiar?',
    problems: [
      { t: 'Tiene un estudiante que no logra descifrar.', d: 'Nada de lo que intenta funciona. Ya lo ha probado todo y se está quedando sin ideas.' },
      { t: 'Sabe que algo anda mal.', d: 'Pero no sabe qué hacer ni a quién llamar. Está haciendo esto solo.' },
      { t: 'Está agotado.', d: 'Y nadie le pregunta cómo está USTED. Usted también importa.' },
    ],

    builtToHelp: 'Esto es lo que creamos para ayudar.',
    features: [
      { title: 'Entrada por voz o texto', desc: 'Describa cualquier dificultad con sus propias palabras' },
      { title: 'Ciencia del cerebro en lenguaje claro', desc: 'Comprenda el porqué detrás de cada conducta' },
      { title: 'Estrategias reales', desc: 'Específicas, prácticas y listas para usar hoy' },
      { title: 'Perfiles de estudiantes', desc: 'Aprende más sobre cada estudiante con el tiempo' },
      { title: 'Apoyo al docente', desc: 'Su bienestar también importa aquí' },
      { title: 'Privacidad incorporada', desc: 'Solo iniciales, nunca nombres completos' },
    ],

    pricingTitle: 'Precios simples y honestos',
    bestValue: 'MEJOR VALOR',
    annualPlan: 'Plan anual',
    annualPrice: '$24.92',
    perMonth: '/mes',
    annualBilled: 'Se factura $299/año · Ahorra $49',
    startFreeTrial: 'Empezar prueba gratis',
    subscribeAnnually: 'Suscribirse anualmente →',
    monthlyPlan: 'Plan mensual',
    monthlyPrice: '$29',
    monthlyBilled: 'Cancele cuando quiera · Sin contratos',
    subscribeMonthly: 'Suscribirse mensualmente →',
    trialNoRenew: 'Su prueba gratis no se renueva automáticamente — no hay cargo a menos que elija un plan.',
    schoolPlan: '¿Plan para escuela o distrito?',

    faqTitle: 'Preguntas que hacen los docentes',
    faqs: [
      { q: '¿Están seguros los datos de los estudiantes?', a: 'Decode My Student nunca almacena nombres completos, números de identificación ni información que identifique a un estudiante. Los docentes usan solo iniciales y grado. Todos los datos se cifran en su navegador y nunca se venden ni se comparten con terceros. La aplicación está diseñada para un uso respetuoso con la privacidad.' },
      { q: '¿Necesito aprobación de la administración?', a: 'No. Por $29 al mes, la mayoría de los docentes se registran por su cuenta y lo declaran como gasto después. No hay nada que instalar en los equipos de la escuela — funciona en cualquier navegador. También hay licencias para escuelas y distritos.' },
      { q: '¿Puedo declararlo como gasto en mi escuela?', a: 'Sí. Muchos docentes declaran Decode My Student como una herramienta de desarrollo profesional. Podemos emitir una factura en el formato que su escuela requiera.' },
    ],

    closingTitle: 'Sus estudiantes necesitan que usted los entienda.',
    closingSubtitle: 'Empiece gratis hoy. Sin tarjeta. Acceso completo por 7 días.',
    closingCta: 'Empezar gratis — Sin tarjeta',
    closingFootnote: 'Hecho para docentes. Basado en la ciencia del cerebro.',
    replayEntrance: 'Volver a ver la entrada al aula',
  },

  safety: {
    heading: 'Esto necesita una persona, no una aplicación.',
    intro: 'Lo que ha descrito podría afectar la seguridad de un estudiante. Decode My Student no opinará sobre esto — por favor, comuníquese con las personas adecuadas ahora.',
    schoolLabel: 'Su escuela es lo primero',
    schoolBody: 'Siga el protocolo de crisis de su escuela y comuníquese ahora con el consejero escolar, un administrador o el equipo de crisis/seguridad.',
    // No se inventa una línea nacional: varía por país. Se oculta este bloque.
    lifelineLabel: '',
    lifelineBody: '',
    lifelineTel: '',
    dangerLabel: 'Peligro inmediato',
    dangerBody: 'Si alguien está en peligro inmediato, llame a su número local de emergencias o al {tel}.',
    dangerTel: '112',
    reporterLabel: 'Si sospecha abuso o negligencia',
    reporterBody: 'Como educador, siga los requisitos de denuncia de protección infantil de su país o región. Si sospecha abuso o negligencia, repórtelo según la política local.',
    internationalLabel: 'Líneas de crisis internacionales',
    internationalBody: 'Encuentre una línea de ayuda en su país en findahelpline.com.',
  },

  disclaimer: {
    text: 'Decode My Student es una herramienta de desarrollo profesional. No diagnostica, no trata ni ofrece consejo clínico sobre ningún niño.',
    terms: 'Términos',
  },

  demoPreview: {
    title: 'Vea lo que recibirá',
    subtitle: 'Describa una situación con sus propias palabras. Este es el tipo de apoyo que verá a cambio.',
    exampleBanner: 'EJEMPLO — no es un estudiante real ni un resultado de IA en vivo',
    grade: 'Grado 5',
    quote: '"Se bloquea cada vez que empieza la escritura independiente."',
    drivingLabel: 'QUÉ PODRÍA ESTAR IMPULSANDO LA CONDUCTA',
    driving: 'Una página en blanco puede sentirse como una amenaza para un sistema nervioso que ya no está seguro de tener éxito. El bloqueo suele ser protección, no desafío — quedarse quieto para evitar una tarea que parece imposible de empezar.',
    tryLabel: 'TRES COSAS PARA INTENTAR HOY',
    tryToday: [
      'Ofrezca elegir por dónde empezar: decir una oración en voz alta antes de escribirla.',
      'Divida la página en blanco en un marco de 3 líneas para que empezar se sienta más pequeño.',
      'Dé un "calentamiento" tranquilo de 2 minutos, sin presión de calificación.',
    ],
    wordsLabel: 'PALABRAS SUGERIDAS PARA USAR',
    words: '"No tienes que hacerlo perfecto — solo empieza. Di tu primera oración en voz alta y la escribiré contigo."',
    escalateLabel: 'CUÁNDO ESCALAR',
    escalate: 'Si los bloqueos se extienden a la mayor parte del día, vienen con señales de desesperanza, o no ceden con apoyo durante algunas semanas, involucre a su consejero o al equipo de apoyo estudiantil.',
    footnote: 'Sus resultados reales son específicos a lo que usted describe y se mantienen privados.',
  },

  footer: {
    terms: 'Términos del servicio',
    privacy: 'Política de privacidad',
    contact: 'Contáctanos',
    rights: '© {year} Brain Health and Wellness Coach Institute. Todos los derechos reservados.',
  },
};

export default es;
